import { useEffect, useRef, useState } from "react";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
// @ts-ignore
import { components } from "react-select";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
// icons
import { fromIcon, poweredByGoogleIcon, toIcon } from "../../common/vector";
// helpers
import { checkIfStartsWithGivenString } from "../../common/utils/helpers/checkIfStartsWithGivenString";
import { getLengthOfStringBeforeGivenWordWithoutSpaces } from "../../common/utils/helpers/getLengthOfStringBeforeGivenWordWithoutSpaces";

const radiusInMeters = 30000;

const CustomGoogleAutocomplete = ({
  setValueOfFirstInput,
  setValueOfSecondInput,
  inputTestIdOfFirstInput,
  inputTestIdOfSecondInput,
  valueOfFirstInput,
  valueOfSecondInput,
  dataCyAttribute = "",
  onClear,
  isLoadingFirstInput,
  isLoadingSecondInput,
  showStar,
  location,
  apiKey,
  isEditableFirstInput = true,
  isEditableSecondInput = true
}) => {
  const theme = useTheme();
  const backgroundColorDefault = "transparent";

  const { t } = useTranslation();

  const [onFirstInputClick, setOnFirstInputClick] = useState(false);
  const [onSecondInputClick, setOnSecondInputClick] = useState(false);

  // COMPUTED WIDTHS FOR HANDLING WIDTH OF MENU LIST
  const [borderWidth, setBorderWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const elementRef = useRef(null);

  useEffect(() => {
    // GET COMPUTED WIDTHS AND UPDATE STATE FOR HANDLING MENU LIST WIDTH
    setBorderWidth(getComputedStyle(elementRef.current)?.borderInlineWidth);
    setContainerWidth(getComputedStyle(elementRef.current)?.width);
  }, []);

  const SingleValueInputFirst = ({ children, ...props }) => {
    const mainText = props?.data?.value?.structured_formatting?.main_text;
    const secondaryText = props?.data?.value?.structured_formatting?.secondary_text;
    return (
      <components.SingleValue {...props}>
        <>
          <div className="main-text">{mainText ? mainText : "/"}</div>
          <div className="secondary-text">{secondaryText ? secondaryText : "/"}</div>
        </>
      </components.SingleValue>
    );
  };
  const SingleValueInputSecond = ({ children, ...props }) => {
    const mainText = props?.data?.value?.structured_formatting?.main_text;
    const secondaryText = props?.data?.value?.structured_formatting?.secondary_text;
    return (
      <components.SingleValue {...props}>
        <>
          <div className="main-text">{mainText ? mainText : "/"}</div>
          <div className="secondary-text">{secondaryText ? secondaryText : "/"}</div>
        </>
      </components.SingleValue>
    );
  };

  const PlaceholderFromInput = props => {
    return (
      <>
        {props?.value || (onFirstInputClick && props.isFocused) ? null : (
          <components.Placeholder {...props}>
            <div>
              <div className="main-text">{t("googleSearch.pickUp")}</div>
              <div className="secondary-text">{t("googleSearch.pickUpInfo")}</div>
            </div>
          </components.Placeholder>
        )}
      </>
    );
  };

  const PlaceholderToInput = props => {
    return (
      <>
        {props?.value || (onSecondInputClick && props.isFocused) ? null : (
          <components.Placeholder {...props}>
            <div className="main-text">{t("googleSearch.dropOff")}</div>
            <div className="secondary-text">{t("googleSearch.dropOffInfo")}</div>
          </components.Placeholder>
        )}
      </>
    );
  };

  const ClearIndicatorFirstInput = props => {
    const handleClearInput = () => {
      props.clearValue();
      setOnFirstInputClick(false);
    };
    return (
      <div
        onClick={handleClearInput}
        onTouchEnd={handleClearInput}
        style={{ display: isEditableFirstInput ? "block" : "none" }}
        data-cy={dataCyAttribute ? `${dataCyAttribute}-clear-from` : ""}
      >
        <div className="remove-x">X</div>
      </div>
    );
  };

  const ClearIndicatorSecondInput = props => {
    const handleClearInput = () => {
      props.clearValue();
      setOnSecondInputClick(false);
    };
    return (
      <div
        onClick={handleClearInput}
        onTouchEnd={handleClearInput}
        style={{ display: isEditableSecondInput ? "block" : "none" }}
        data-cy={dataCyAttribute ? `${dataCyAttribute}-clear-to` : ""}
      >
        <div className="remove-x">X</div>
      </div>
    );
  };

  const MenuList = props => {
    return (
      <div
        className="menu-list"
        data-cy={dataCyAttribute ? `${dataCyAttribute}-menu` : ""}
        style={{
          width: containerWidth,
          left: "-" + borderWidth,
          zIndex: 1000,
          position: "relative",
          opacity: 1,
          display: props?.selectProps?.inputValue ? "block" : "none"
        }}
      >
        {props?.selectProps?.inputValue !== "" ? (
          props.children
        ) : (
          <div
            style={{
              boxSizing: "border-box",
              color: "hsl(0, 0%, 60%)",
              padding: "8px 12px",
              textAlign: "left",
              zIndex: 1000
            }}
          >
            {t("googleSearch.typeToSearch")}
          </div>
        )}
        <div
          style={{
            position: "relative",
            paddingBottom: "30px",
            zIndex: 1000
          }}
        >
          <img
            style={{ position: "absolute", bottom: "10px", left: "15px", height: "10px", width: "auto", zIndex: 1000 }}
            src={poweredByGoogleIcon?.default}
            alt="google"
          />
        </div>
      </div>
    );
  };

  const Options = props => {
    const lengthOfInputValue = props?.selectProps.inputValue?.replace(/\s/g, "").split("")?.length;
    const inputValueSplitOnWordsArr = props?.selectProps?.inputValue.split(" ");
    const placeId = props?.data?.value?.place_id;
    const mainText = props?.data?.value?.structured_formatting?.main_text;
    const optionKey = placeId || mainText || "option";
    return (
      <components.Option
        {...props}
        className="option"
        data-cy={dataCyAttribute ? `${dataCyAttribute}-option-${optionKey}` : ""}
      >
        <div className="main-text">
          {props?.data?.value?.structured_formatting?.main_text.split(" ").map((word, index) => {
            return (
              <span key={index}>
                {index > 0 && <span> </span>}
                {word.split("").map((letter, letterIndex) =>
                  (letterIndex <
                    inputValueSplitOnWordsArr.find(inputValueWord => checkIfStartsWithGivenString(word, inputValueWord))
                      ?.length &&
                    inputValueSplitOnWordsArr.find(inputValueWord =>
                      checkIfStartsWithGivenString(word, inputValueWord)
                    )) ||
                  (letterIndex <
                    lengthOfInputValue -
                      (index > 0
                        ? getLengthOfStringBeforeGivenWordWithoutSpaces(
                            props?.data?.value?.structured_formatting?.main_text,
                            index
                          )
                        : 0) &&
                    checkIfStartsWithGivenString(
                      props?.data?.value?.structured_formatting?.main_text,
                      props?.selectProps?.inputValue
                    )) ||
                  (letterIndex < word?.split("").length &&
                    checkIfStartsWithGivenString(
                      props?.selectProps?.inputValue,
                      props?.data?.value?.structured_formatting?.main_text
                    )) ? (
                    <span key={letterIndex} className="main-text--bold">
                      {letter}
                    </span>
                  ) : (
                    <span key={letterIndex}>{letter}</span>
                  )
                )}
              </span>
            );
          })}
        </div>
        <div className="secondary-text">{props.data?.value?.structured_formatting?.secondary_text}</div>
      </components.Option>
    );
  };

  return (
    <div>
      <div className="row__label row__label-align">
        {t("googleSearch.title")} {showStar && <span className="mandatory-star">*</span>}
      </div>

      <div ref={elementRef} className={`custom-google-places custom-google-places`} data-cy={dataCyAttribute || ""}>
        <div className="custom-google-places__input-box" style={{ zIndex: 2 }}>
          <img className="custom-google-places__icon" src={fromIcon?.default} alt="from" />
          <GooglePlacesAutocomplete
            apiKey={apiKey}
            selectProps={{
              isClearable: true,
              getOptionLabel: options => (
                <div className="option">
                  <div className="main-text">{options.value.structured_formatting.main_text}</div>
                  <div className="secondary-text">{options.value.structured_formatting.secondary_text}</div>
                </div>
              ),
              inputId: inputTestIdOfFirstInput,
              value: valueOfFirstInput,
              isLoading: isLoadingFirstInput,
              onChange: (address, action) => {
                if (action.action === "clear" && onClear) {
                  onClear("first_input");
                }
                setValueOfFirstInput(address, action);
              },
              onMenuOpen: value => {
                setOnFirstInputClick(true);
                // SET ACCURATE VALUES IN CASE WIDTH OF SCREEN IS CHANGED OR RESIZED
                setBorderWidth(getComputedStyle(elementRef.current)?.borderInlineWidth);
                setContainerWidth(getComputedStyle(elementRef.current)?.width);
              },
              onBlur: value => {
                setOnFirstInputClick(false);
              },
              isSearchable: !valueOfFirstInput,
              openMenuOnClick: !valueOfFirstInput,
              classNamePrefix: "react-select",
              styles: {
                menu: defaultStyles => ({
                  ...defaultStyles
                }),
                indicatorSeparator: defaultStyles => ({
                  ...defaultStyles,
                  display: "none" // hide indicator separator
                }),
                dropdownIndicator: defaultStyles => ({
                  ...defaultStyles,
                  display: "none" // hide dropdown container
                }),
                popupIndicator: defaultStyles => ({
                  ...defaultStyles,
                  backgroundImage: `url(${poweredByGoogleIcon}) !important`,
                  width: "20px !important",
                  height: "20px !important"
                }),
                clearIndicator: defaultStyles => ({
                  ...defaultStyles,
                  color: theme.palette.primary.light,
                  marginRight: 4,
                  padding: "8px",
                  cursor: "pointer",
                  "&:hover": {
                    color: theme.palette.primary.light,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "50%"
                  }
                }),
                placeholder: base => ({
                  ...base,
                  margin: 0,
                  padding: 0
                }),
                container: base => ({
                  ...base,
                  flex: 1
                }),
                singleValue: base => ({
                  ...base,
                  margin: 0,
                  minHeight: "30px"
                }),
                control: base => ({
                  ...base,
                  paddingLeft: "30px",
                  flex: 1,
                  backgroundColor: backgroundColorDefault,
                  border: "none",
                  outline: "none",
                  width: "100%",
                  borderRadius: 0,
                  boxShadow: "none",
                  cursor: "text",
                  "&:hover": {
                    border: "none"
                  }
                }),
                option: (styles, { isFocused }) => {
                  return {
                    ...styles,
                    backgroundColor: isFocused ? theme.palette.grayLight.main : "transparent",
                    color: "black",
                    "&:active": {
                      background: `${theme.palette.grayLight.main} !important`
                    }
                  };
                },
                input: () => ({
                  fontFamily: `Neutraface2Book`,
                  minHeight: "38px",
                  lineHeight: "38px",
                  "& input": {
                    font: "inherit"
                  }
                }),
                noOptionsMessage: base => ({
                  ...base,
                  textAlign: "left"
                }),

                loadingMessage: base => ({
                  ...base,
                  textAlign: "left"
                })
              },
              components: {
                Placeholder: PlaceholderFromInput,
                SingleValue: SingleValueInputFirst,
                MenuList: MenuList,
                Option: Options,
                ClearIndicator: ClearIndicatorFirstInput
              }
            }}
            autocompletionRequest={{
              radius: radiusInMeters,
              location: location
            }}
          />
        </div>
        <div className="custom-google-places__divider">
          <span></span>
        </div>
        <div className="custom-google-places__input-box" style={{ zIndex: 1 }}>
          <img className="custom-google-places__icon " src={toIcon?.default} alt="to" />
          <GooglePlacesAutocomplete
            apiKey={apiKey}
            selectProps={{
              isClearable: true,
              getOptionLabel: options => {
                return (
                  <div>
                    <div className="main-text">{options.value.structured_formatting.main_text}</div>
                    <div className="secondary-text">{options.value.structured_formatting.secondary_text}</div>
                  </div>
                );
              },
              inputId: inputTestIdOfSecondInput,
              value: valueOfSecondInput,
              isLoading: isLoadingSecondInput,
              onChange: (address, action) => {
                if (action.action === "clear" && onClear) {
                  onClear("second_input");
                }
                setValueOfSecondInput(address, action);
              },
              onMenuOpen: value => {
                setOnSecondInputClick(true);
                // SET ACCURATE VALUES IN CASE WIDTH OF SCREEN IS CHANGED OR RESIZED
                setBorderWidth(getComputedStyle(elementRef.current)?.borderInlineWidth);
                setContainerWidth(getComputedStyle(elementRef.current)?.width);
              },
              onBlur: value => {
                setOnSecondInputClick(false);
              },
              isSearchable: !valueOfSecondInput,
              openMenuOnClick: !valueOfSecondInput,
              classNamePrefix: "react-select",
              styles: {
                menu: defaultStyles => ({
                  ...defaultStyles
                }),
                indicatorSeparator: defaultStyles => ({
                  ...defaultStyles,
                  display: "none" // hide indicator separator
                }),
                dropdownIndicator: defaultStyles => ({
                  ...defaultStyles,
                  display: "none" // hide dropdown container
                }),
                popupIndicator: defaultStyles => ({
                  ...defaultStyles,
                  backgroundImage: `url(${poweredByGoogleIcon}) !important`,
                  width: "20px !important",
                  height: "20px !important"
                }),
                clearIndicator: defaultStyles => ({
                  ...defaultStyles,
                  color: theme.palette.primary.light,
                  marginRight: 4,
                  padding: "8px",
                  cursor: "pointer",
                  "&:hover": {
                    color: theme.palette.primary.light,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "50%"
                  }
                }),
                placeholder: base => ({
                  ...base,
                  margin: 0,
                  padding: 0
                }),
                container: base => ({
                  ...base,
                  flex: 1
                }),
                singleValue: base => ({
                  ...base,
                  margin: 0,
                  minHeight: "30px"
                }),
                control: base => ({
                  ...base,
                  padding: 0,
                  paddingLeft: "30px",
                  flex: 1,
                  backgroundColor: backgroundColorDefault,
                  border: "none",
                  outline: "none",
                  width: "100%",
                  borderRadius: 0,
                  boxShadow: "none",
                  cursor: "text",
                  "&:hover": {
                    border: "none"
                  }
                }),
                option: (styles, { isFocused }) => {
                  return {
                    ...styles,
                    backgroundColor: isFocused ? theme.palette.grayLight.main : "transparent",
                    color: "black",
                    "&:active": {
                      background: `${theme.palette.grayLight.main} !important`
                    }
                  };
                },
                input: () => ({
                  fontFamily: `Neutraface2Book`,
                  minHeight: "38px",
                  lineHeight: "38px",
                  "& input": {
                    font: "inherit"
                  }
                }),
                noOptionsMessage: base => ({
                  ...base,
                  textAlign: "left"
                }),

                loadingMessage: base => ({
                  ...base,
                  textAlign: "left"
                })
              },
              components: {
                Placeholder: PlaceholderToInput,
                SingleValue: SingleValueInputSecond,
                MenuList: MenuList,
                Option: Options,
                ClearIndicator: ClearIndicatorSecondInput
              }
            }}
            autocompletionRequest={{
              radius: radiusInMeters,
              location: location
            }}
          />
        </div>
      </div>
    </div>
  );
};

CustomGoogleAutocomplete.defaultProps = {
  showStar: false,
  isLoadingFirstInput: false,
  isLoadingSecondInput: false,
  dataCyAttribute: ""
};

export default CustomGoogleAutocomplete;
